import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Paper
} from '@mui/material'
import {
  ArrowForward as ArrowForwardIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  EmojiEvents as TrophyIcon,
  Lightbulb as LightbulbIcon
} from '@mui/icons-material'
import SEO from '@components/SEO'

const AboutPage: React.FC = () => {
  const navigate = useNavigate()

  const team = [
    {
      name: 'David O\'Hara',
      role: 'Founder & CEO',
      avatar: 'DO',
      bio: 'Passionate cigar enthusiast with 15+ years of collecting experience. Founded CigarTrack to solve the challenges he faced managing his own collection.'
    },
    {
      name: 'Sarah Chen',
      role: 'Lead Developer',
      avatar: 'SC',
      bio: 'Full-stack developer with expertise in React and Node.js. Dedicated to creating intuitive user experiences for cigar collectors.'
    },
    {
      name: 'Michael Rodriguez',
      role: 'Product Designer',
      avatar: 'MR',
      bio: 'UX/UI designer focused on creating beautiful, functional interfaces that make cigar collection management enjoyable and efficient.'
    },
    {
      name: 'Jennifer Kim',
      role: 'Community Manager',
      avatar: 'JK',
      bio: 'Cigar community expert who helps users get the most out of CigarTrack and builds connections within the cigar enthusiast community.'
    }
  ]

  const values = [
    {
      icon: <LightbulbIcon sx={{ fontSize: 40, color: '#8B4513' }} />,
      title: 'Innovation',
      description: 'We continuously innovate to provide the best tools and features for cigar collection management.'
    },
    {
      icon: <GroupIcon sx={{ fontSize: 40, color: '#8B4513' }} />,
      title: 'Community',
      description: 'We believe in building a strong community of cigar enthusiasts who can learn and grow together.'
    },
    {
      icon: <TrophyIcon sx={{ fontSize: 40, color: '#8B4513' }} />,
      title: 'Excellence',
      description: 'We strive for excellence in everything we do, from our product design to customer support.'
    }
  ]

  const milestones = [
    {
      year: '2024',
      title: 'CigarTrack Founded',
      description: 'Started with a vision to revolutionize cigar collection management'
    },
    {
      year: '2024',
      title: 'Beta Launch',
      description: 'Launched beta version with core collection management features'
    },
    {
      year: '2024',
      title: 'Public Launch',
      description: 'Released to the public with advanced analytics and community features'
    },
    {
      year: '2026',
      title: 'Mobile Apps Coming Soon',
      description: 'Native iOS and Android apps are in development and coming soon'
    }
  ]

  return (
    <Box>
      <SEO
        title="About Us - CigarTrack"
        description="Learn about the team behind CigarTrack and our mission to help cigar enthusiasts manage and enjoy their collections."
        keywords="about CigarTrack, cigar app team, cigar enthusiast platform"
        url="/about"
      />
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
          color: 'white',
          py: { xs: 6, md: 8 }
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                lineHeight: 1.2
              }}
            >
              About CigarTrack
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                maxWidth: 800,
                mx: 'auto'
              }}
            >
              Born from a passion for cigars and the need for better collection management tools, 
              CigarTrack is the ultimate platform for cigar enthusiasts worldwide.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Our Story Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                component="h2"
                gutterBottom
                sx={{ color: '#8B4513', fontWeight: 600 }}
              >
                Our Story
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                CigarTrack was born from a simple problem: as our founder&apos;s cigar collection grew, 
                keeping track of everything became increasingly difficult. Spreadsheets were clunky, 
                paper logs got lost, and there was no centralized way to manage multiple humidors 
                while tracking the aging and enjoyment of each cigar.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
                We realized that cigar enthusiasts needed a modern, digital solution that could 
                handle the complexity of serious collection management while remaining intuitive 
                and enjoyable to use. That&apos;s when CigarTrack was conceived.
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.7 }}>
                Today, CigarTrack serves cigar enthusiasts worldwide, from casual 
                collectors to serious aficionados, helping them organize, track, and enhance 
                their cigar experience.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register')}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  backgroundColor: '#8B4513',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#7A3D0F'
                  }
                }}
              >
                Join Our Community
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={8}
                sx={{
                  p: 4,
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  height: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Box textAlign="center">
                  <BusinessIcon sx={{ fontSize: 80, color: '#8B4513', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Our Mission
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    To revolutionize how cigar enthusiasts manage and enjoy their collections
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Values Section */}
      <Box sx={{ py: 8, backgroundColor: '#F5F5DC' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: '#8B4513', fontWeight: 600, mb: 6 }}
          >
            Our Values
          </Typography>
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 4,
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 3 }}>
                      {value.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ color: '#8B4513', fontWeight: 600 }}>
                      {value.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Team Section */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: '#8B4513', fontWeight: 600, mb: 6 }}
          >
            Meet Our Team
          </Typography>
          <Grid container spacing={4}>
            {team.map((member, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ height: '100%', textAlign: 'center' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        bgcolor: '#8B4513',
                        fontSize: '2rem'
                      }}
                    >
                      {member.avatar}
                    </Avatar>
                    <Typography variant="h6" gutterBottom sx={{ color: '#8B4513', fontWeight: 600 }}>
                      {member.name}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      {member.role}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {member.bio}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Timeline Section */}
      <Box sx={{ py: 8, backgroundColor: '#F5F5DC' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: '#8B4513', fontWeight: 600, mb: 6 }}
          >
            Our Journey
          </Typography>
          <Grid container spacing={4}>
            {milestones.map((milestone, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h4"
                      component="div"
                      sx={{ color: '#8B4513', fontWeight: 700, mb: 1 }}
                    >
                      {milestone.year}
                    </Typography>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {milestone.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {milestone.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
          color: 'white',
          py: 8
        }}
      >
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              Ready to Join Our Community?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Be part of the growing community of cigar enthusiasts who trust CigarTrack 
              to manage and enhance their collections.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              endIcon={<ArrowForwardIcon />}
              sx={{
                backgroundColor: 'white',
                color: '#8B4513',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Get Started Today
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  )
}

export default AboutPage

